/**
 * Valid replaceable variables for onlineBooking.communication templates.
 * Keys match formik communication field names.
 */

/** API template identifiers used for GET /api/communication/templates */
export const COMMUNICATION_TEMPLATE_IDENTIFIERS = [
    'OTP',
    'BOOKING',
    'BOOKING_RESCHEDULE',
    'BOOKING_CANCEL',
    'REMINDER',
    'MANUAL_INFO_PENDING',
    'MANUAL_FORM_CONFIRMATION',
    'ONLINE_FORM_CONFIRMATION',
    'HEALTH_DECLARATION_INITIAL',
    'HEALTH_DECLARATION_REMINDER',
];

/** Maps API identifier → formik communication field key */
export const COMMUNICATION_TEMPLATE_FIELD_MAP = {
    OTP: 'OTP',
    BOOKING: 'bookingConfirmation',
    BOOKING_RESCHEDULE: 'bookingReschedule',
    BOOKING_CANCEL: 'cancellation',
    REMINDER: 'reminder',
    MANUAL_INFO_PENDING: 'EmailFormrequest',
    MANUAL_FORM_CONFIRMATION: 'EmailFormConfirm',
    ONLINE_FORM_CONFIRMATION: 'EmailOnlineFormConfirm',
    HEALTH_DECLARATION_INITIAL: 'health_declaration_initial_email',
    HEALTH_DECLARATION_REMINDER: 'health_declaration_reminder_email',
};

/**
 * Pick outlet template if present, otherwise default.
 */
export const pickCommunicationTemplate = (templates, identifier, mode) =>
    templates?.[identifier]?.[mode]?.outlet || templates?.[identifier]?.[mode]?.default || null;

/**
 * Map API templates response into formik communication body/subject fields.
 * Single source of truth: outlet || default from templates API.
 */
export const mapCommunicationTemplatesFromApi = (templates) => {
    const mapped = {};

    Object.entries(COMMUNICATION_TEMPLATE_FIELD_MAP).forEach(([identifier, fieldKey]) => {
        const smsTemplate = pickCommunicationTemplate(templates, identifier, 'SMS');
        const emailTemplate = pickCommunicationTemplate(templates, identifier, 'EMAIL');
        const isHealthDeclaration = fieldKey.startsWith('health_declaration');

        mapped[fieldKey] = {
            sms_body: smsTemplate?.template ?? '',
            email_body: emailTemplate?.template ?? '',
            ...(isHealthDeclaration
                ? { email_subject: emailTemplate?.subject ?? '' }
                : { email_sub: emailTemplate?.subject ?? '' }),
        };
    });

    return mapped;
};

export const COMMUNICATION_EMAIL_VARIABLES = {
    OTP: ['otp'],
    bookingConfirmation: [
        'service_list',
        'booking_date',
        'booking_time',
        'outlet_contact_number',
        'outlet_name',
        'outlet_address',
        'customer_name',
        'employee_name',
        'cancellation_policy_style',
        'cancellation_policy_url',
        'booking_link',
    ],
    reminder: [
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_name',
        'outlet_contact_number',
        'outlet_address',
        'customer_name',
        'employee_name',
        'booking_link',
    ],
    bookingReschedule: [
        'service_list',
        'booking_date',
        'booking_time',
        'outlet_contact_number',
        'outlet_name',
        'outlet_address',
        'customer_name',
        'employee_name',
        'booking_link',
    ],
    cancellation: [
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_contact_number',
        'outlet_name',
        'customer_name',
        'employee_name',
    ],
    EmailFormrequest: ['customer_name', 'outlet_name', 'form_link'],
    EmailFormConfirm: [
        'customer_name',
        'outlet_name',
        'outlet_contact_number',
        'cancellation_policy_style',
        'cancellation_policy_url',
    ],
    EmailOnlineFormConfirm: [
        'customer_name',
        'outlet_name',
        'outlet_contact_number',
        'cancellation_policy_style',
        'cancellation_policy_url',
    ],
    health_declaration_initial_email: [
        'customer_name',
        'outlet_name',
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_address',
        'booking_link',
    ],
    health_declaration_reminder_email: [
        'customer_name',
        'outlet_name',
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_address',
        'booking_link',
    ],
};

export const COMMUNICATION_SMS_VARIABLES = {
    OTP: ['otp'],
    bookingConfirmation: [
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_contact_number',
        'outlet_name',
        'outlet_address',
        'customer_name',
        'employee_name',
    ],
    reminder: [
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_name',
        'outlet_contact_number',
        'outlet_address',
        'customer_name',
        'employee_name',
    ],
    bookingReschedule: [
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_contact_number',
        'outlet_name',
        'outlet_address',
        'customer_name',
        'employee_name',
    ],
    cancellation: [
        'service_name',
        'booking_date',
        'booking_time',
        'outlet_contact_number',
        'outlet_name',
        'employee_name',
    ],
};

export const formatTemplateVariables = (variables = []) => variables.map((variable) => `\${${variable}}`).join(', ');
